import React from "react";
import { Controller, Control, FieldErrors, UseFormSetValue, useWatch } from "react-hook-form";
import { Form, Spinner } from "react-bootstrap";
import { useAuth } from "../../../../auth/context/AuthProvider";
import useIndexOtaChannel from "../../../../services/ota-channel/hooks/useIndexOtaChannel";

interface OtaSelectComponentProps {
    control: Control<any>;
    errors: FieldErrors<{ filter: { ota_code?: string } }>;
    setValue: UseFormSetValue<any>;
}

// OTA and Agent are mutually exclusive — a booking is sourced from one or the
// other, never both, so picking one here clears and disables the Agent filter.
const OtaSelectComponent: React.FC<OtaSelectComponentProps> = ({ control, errors, setValue }) => {
    const { user } = useAuth();
    const { data: otas, isFetching, error } = useIndexOtaChannel({
        all: true,
        filter: { property_id: user?.active_property_id },
    });

    const agentId = useWatch({ control, name: "filter.agent_id" });
    const disabled = !!agentId;

    return (
        <div>
            <Form.Label className="me-2">Filter OTA:</Form.Label>

            {isFetching ? (
                <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading OTAs...</span>
                </div>
            ) : error ? (
                <div className="text-danger">Failed to load OTAs</div>
            ) : (
                <Controller
                    name="filter.ota_code"
                    control={control}
                    render={({ field }) => (
                        <>
                            <Form.Select
                                {...field}
                                value={field.value ?? ""}
                                isInvalid={!!errors.filter?.ota_code}
                                disabled={disabled}
                                onChange={(e) => {
                                    field.onChange(e);
                                    if (e.target.value) {
                                        setValue("filter.agent_id", "");
                                    }
                                }}
                            >
                                <option value="">{disabled ? "Disabled (Agent selected)" : "All OTAs"}</option>
                                {otas?.data.map((ota) => (
                                    <option key={ota.id} value={ota.code}>
                                        {ota.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {errors.filter?.ota_code && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.filter.ota_code.message as string}
                                </Form.Control.Feedback>
                            )}
                        </>
                    )}
                />
            )}
        </div>
    );
};

export default OtaSelectComponent;
